import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { BarChart3, TrendingUp, DollarSign, Clock, RefreshCw, X, Trash2, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportsPage = () => {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [report, setReport] = useState(null);
  const [cancelledOrders, setCancelledOrders] = useState([]);
  const [unpaidOrders, setUnpaidOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [showUnpaidModal, setShowUnpaidModal] = useState(false);

  // Raporu yükle
  const loadReport = useCallback(async () => {
    setLoading(true);
    console.log("[ReportsPage] Rapor yükleniyor, tarih:", date);
    try {
      const response = await axios.get(`${API}/reports/daily?date=${date}`);
      console.log("[ReportsPage] Rapor verisi:", response.data);
      console.log("[ReportsPage] Bekleyen sipariş:", response.data.unpaid_orders);
      console.log("[ReportsPage] Bekleyen tutar:", response.data.unpaid_revenue);
      setReport(response.data);
      setLastRefresh(new Date().toLocaleTimeString('tr-TR'));
    } catch (error) {
      console.error("[ReportsPage] Rapor yüklenirken hata:", error);
    }
    setLoading(false);
  }, [date]);

  // İptal edilen siparişleri yükle
  const loadCancelledOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      // İptal edilen siparişleri filtrele
      const cancelled = response.data.filter(
        order => order.payment_status === "iptal" && 
                 order.created_at?.startsWith(date)
      );
      console.log("[ReportsPage] İptal edilen siparişler:", cancelled.length);
      setCancelledOrders(cancelled);
      
      // Bekleyen siparişleri de yükle
      const unpaid = response.data.filter(
        order => order.payment_status === "ödenmedi"
      );
      console.log("[ReportsPage] Bekleyen siparişler:", unpaid.length);
      setUnpaidOrders(unpaid);
    } catch (error) {
      console.error("[ReportsPage] Siparişler yüklenirken hata:", error);
    }
  }, [date]);

  // Sayfa yüklendiğinde ve tarih değiştiğinde raporu yükle
  useEffect(() => {
    loadReport();
    loadCancelledOrders();
  }, [loadReport, loadCancelledOrders]);

  // Her 30 saniyede otomatik yenile
  useEffect(() => {
    const interval = setInterval(() => {
      console.log("[ReportsPage] Otomatik yenileme...");
      loadReport();
      loadCancelledOrders();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [loadReport, loadCancelledOrders]);

  // Manuel yenileme
  const handleRefresh = () => {
    console.log("[ReportsPage] Manuel yenileme...");
    loadReport();
    loadCancelledOrders();
  };

  // Bekleyen siparişi sil
  const deleteUnpaidOrder = async (orderId) => {
    if (!window.confirm("Bu bekleyen siparişi silmek istediğinize emin misiniz?")) {
      return;
    }
    try {
      await axios.delete(`${API}/orders/${orderId}`);
      console.log("[ReportsPage] Bekleyen sipariş silindi:", orderId);
      loadReport();
      loadCancelledOrders();
    } catch (error) {
      console.error("[ReportsPage] Sipariş silinirken hata:", error);
      alert("Sipariş silinirken hata oluştu");
    }
  };

  // Tüm bekleyen siparişleri sil
  const deleteAllUnpaidOrders = async () => {
    if (!window.confirm(`${unpaidOrders.length} bekleyen siparişi silmek istediğinize emin misiniz? Bu işlem geri alınamaz!`)) {
      return;
    }
    try {
      for (const order of unpaidOrders) {
        await axios.delete(`${API}/orders/${order.id}`);
      }
      console.log("[ReportsPage] Tüm bekleyen siparişler silindi");
      setShowUnpaidModal(false);
      loadReport();
      loadCancelledOrders();
    } catch (error) {
      console.error("[ReportsPage] Siparişler silinirken hata:", error);
      alert("Siparişler silinirken hata oluştu");
    }
  };

  if (loading && !report) {
    return (
      <div className="p-8 flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Rapor yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="p-8 flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-gray-600">Rapor yüklenemedi. Lütfen sayfayı yenileyin.</p>
          <button 
            onClick={handleRefresh}
            className="mt-4 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
          >
            Yenile
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="reports-page">
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center space-x-4">
          <h1 className="text-3xl font-bold text-gray-800">Günlük Rapor</h1>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className={`p-2 rounded-lg border-2 border-gray-300 hover:bg-gray-100 transition ${loading ? 'animate-spin' : ''}`}
            title="Yenile"
          >
            <RefreshCw size={20} className="text-gray-600" />
          </button>
          {lastRefresh && (
            <span className="text-sm text-gray-500">
              Son güncelleme: {lastRefresh}
            </span>
          )}
        </div>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="px-4 py-2 border-2 rounded-lg text-lg"
        />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <BarChart3 size={32} className="opacity-80" />
            <span className="text-3xl font-bold">{report.total_orders}</span>
          </div>
          <p className="text-sm opacity-90">Toplam Sipariş</p>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <DollarSign size={32} className="opacity-80" />
            <span className="text-3xl font-bold">{report.daily_revenue ? report.daily_revenue.toFixed(0) : report.total_revenue.toFixed(0)}</span>
          </div>
          <p className="text-sm opacity-90">Günlük Ciro (₺)</p>
          <p className="text-xs opacity-75 mt-1">Nakit + Kredi Kartı</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp size={32} className="opacity-80" />
            <span className="text-3xl font-bold">{report.paid_orders}</span>
          </div>
          <p className="text-sm opacity-90">Ödenen Siparişler</p>
        </div>

        {/* Bekleyen Ödemeler - Tıklanabilir */}
        <div 
          className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg cursor-pointer hover:from-orange-600 hover:to-orange-700 transition"
          onClick={() => report.unpaid_orders > 0 && setShowUnpaidModal(true)}
        >
          <div className="flex items-center justify-between mb-2">
            <Clock size={32} className="opacity-80" />
            <span className="text-3xl font-bold">{report.unpaid_orders}</span>
          </div>
          <p className="text-sm opacity-90">Bekleyen Ödemeler</p>
          {report.unpaid_orders > 0 && (
            <p className="text-xs opacity-75 mt-1">Detay için tıklayın</p>
          )}
        </div>
      </div>

      {/* Bekleyen Ödemeler Modal */}
      {showUnpaidModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="bg-orange-500 text-white p-4 flex justify-between items-center">
              <h2 className="text-xl font-bold flex items-center">
                <AlertTriangle className="mr-2" size={24} />
                Bekleyen Ödemeler ({unpaidOrders.length})
              </h2>
              <button 
                onClick={() => setShowUnpaidModal(false)}
                className="hover:bg-orange-600 rounded-full p-1"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[50vh]">
              {unpaidOrders.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Bekleyen ödeme yok</p>
              ) : (
                <div className="space-y-3">
                  {unpaidOrders.map((order) => (
                    <div key={order.id} className="border-2 border-orange-200 bg-orange-50 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-bold text-gray-800">
                            {order.order_type === "masa" ? (
                              <>🪑 Masa: {order.table_number || "?"}</>
                            ) : order.order_type === "paket" ? (
                              <>📦 Paket: {order.customer_name || "Misafir"}</>
                            ) : (
                              <>🏃 Gel-Al: {order.customer_name || "Misafir"}</>
                            )}
                          </h3>
                          {order.customer_phone && (
                            <p className="text-sm text-gray-600">Tel: {order.customer_phone}</p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            {order.created_at ? new Date(order.created_at).toLocaleString("tr-TR") : "Tarih yok"}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-orange-600">{order.total?.toFixed(2)} ₺</p>
                          <button
                            onClick={() => deleteUnpaidOrder(order.id)}
                            className="mt-2 px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 flex items-center"
                          >
                            <Trash2 size={14} className="mr-1" /> Sil
                          </button>
                        </div>
                      </div>
                      
                      {/* Ürünler */}
                      <div className="mt-3 bg-white rounded p-2">
                        <p className="text-xs font-semibold text-gray-600 mb-1">Ürünler:</p>
                        {order.items?.map((item, idx) => (
                          <div key={idx} className="text-sm text-gray-700">
                            • {item.quantity}x {item.product_name} ({(item.price * item.quantity).toFixed(2)} ₺)
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {unpaidOrders.length > 0 && (
              <div className="border-t p-4 bg-gray-50">
                <div className="flex justify-between items-center mb-3">
                  <span className="font-bold text-gray-700">Toplam Bekleyen:</span>
                  <span className="text-2xl font-bold text-orange-600">
                    {unpaidOrders.reduce((sum, o) => sum + (o.total || 0), 0).toFixed(2)} ₺
                  </span>
                </div>
                <button
                  onClick={deleteAllUnpaidOrders}
                  className="w-full py-3 bg-red-500 text-white font-bold rounded-lg hover:bg-red-600 flex items-center justify-center"
                >
                  <Trash2 size={20} className="mr-2" />
                  Tüm Bekleyen Siparişleri Sil
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Revenue Breakdown */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Ciro Detayı</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600 flex items-center">
                Günlük Ciro 
                <span className="text-xs ml-2 bg-green-100 text-green-700 px-2 py-1 rounded">
                  Nakit + Kredi Kartı
                </span>
              </span>
              <span className="text-2xl font-bold text-green-600">
                {report.daily_revenue ? report.daily_revenue.toFixed(2) : report.total_revenue.toFixed(2)} ₺
              </span>
            </div>
            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Ödenen (Tümü)</span>
              <span className="text-xl font-bold text-blue-600">{report.paid_revenue.toFixed(2)} ₺</span>
            </div>
            <div className="flex justify-between items-center py-3">
              <span className="text-gray-600">Bekleyen</span>
              <span className="text-xl font-bold text-orange-600">{report.unpaid_revenue.toFixed(2)} ₺</span>
            </div>
          </div>
        </div>

        {/* Order Types */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Sipariş Tipleri</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-gray-600">Paket Servis</span>
              </div>
              <span className="text-xl font-bold text-gray-800">{report.orders_by_type.paket || 0}</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-gray-600">Gel-Al</span>
              </div>
              <span className="text-xl font-bold text-gray-800">{report.orders_by_type["gel-al"] || 0}</span>
            </div>
            <div className="flex justify-between items-center py-3">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                <span className="text-gray-600">Masa</span>
              </div>
              <span className="text-xl font-bold text-gray-800">{report.orders_by_type.masa || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Methods Breakdown */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Ödeme Yöntemleri (Gün Sonu)</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* Nakit */}
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">💵 Nakit</h3>
              <span className="text-sm bg-green-200 text-green-800 px-3 py-1 rounded-full font-medium">
                {report.payment_methods?.nakit?.count || 0} Sipariş
              </span>
            </div>
            <p className="text-3xl font-bold text-green-600">
              {(report.payment_methods?.nakit?.total || 0).toFixed(2)} ₺
            </p>
          </div>

          {/* Kredi Kartı */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">💳 Kredi Kartı</h3>
              <span className="text-sm bg-blue-200 text-blue-800 px-3 py-1 rounded-full font-medium">
                {report.payment_methods?.kredi_karti?.count || 0} Sipariş
              </span>
            </div>
            <p className="text-3xl font-bold text-blue-600">
              {(report.payment_methods?.kredi_karti?.total || 0).toFixed(2)} ₺
            </p>
          </div>

          {/* Yemek Kartı */}
          <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">🎫 Yemek Kartı</h3>
              <span className="text-sm bg-purple-200 text-purple-800 px-3 py-1 rounded-full font-medium">
                {report.payment_methods?.yemek_karti?.count || 0} Sipariş
              </span>
            </div>
            <p className="text-3xl font-bold text-purple-600 mb-4">
              {(report.payment_methods?.yemek_karti?.total || 0).toFixed(2)} ₺
            </p>
            
            {/* Yemek Kartı Detayları */}
            <div className="space-y-2 border-t border-purple-200 pt-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Sodexo:</span>
                <span className="font-semibold text-purple-700">
                  {(report.payment_methods?.yemek_karti?.breakdown?.sodexo || 0).toFixed(2)} ₺
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Ticket:</span>
                <span className="font-semibold text-purple-700">
                  {(report.payment_methods?.yemek_karti?.breakdown?.ticket || 0).toFixed(2)} ₺
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Multinet:</span>
                <span className="font-semibold text-purple-700">
                  {(report.payment_methods?.yemek_karti?.breakdown?.multinet || 0).toFixed(2)} ₺
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Metropol:</span>
                <span className="font-semibold text-purple-700">
                  {(report.payment_methods?.yemek_karti?.breakdown?.metropol || 0).toFixed(2)} ₺
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* İptal Edilen Siparişler */}
      {cancelledOrders.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6 mt-6">
          <h2 className="text-2xl font-bold text-red-600 mb-6">
            ❌ İptal Edilen Siparişler ({cancelledOrders.length})
          </h2>
          
          <div className="space-y-4">
            {cancelledOrders.map((order) => (
              <div key={order.id} className="border-2 border-red-200 bg-red-50 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-bold text-gray-800 text-lg">
                      {order.order_type === "masa" ? (
                        <>🪑 {order.table_number}</>
                      ) : (
                        <>📞 {order.customer_name || "Misafir"}</>
                      )}
                    </h3>
                    {order.order_type !== "masa" && order.customer_phone && (
                      <p className="text-sm text-gray-600">Tel: {order.customer_phone}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(order.created_at).toLocaleString("tr-TR")}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-red-600">{order.total} ₺</p>
                    <p className="text-xs text-gray-600 mt-1">
                      {order.order_type === "paket" ? "📦 Paket" : order.order_type === "gel-al" ? "🏃 Gel-Al" : "🪑 Masa"}
                    </p>
                  </div>
                </div>

                {/* Ürünler */}
                <div className="bg-white rounded p-3 mb-3">
                  <p className="text-xs font-semibold text-gray-600 mb-2">Sipariş İçeriği:</p>
                  <div className="space-y-1">
                    {order.items?.map((item, idx) => (
                      <div key={idx} className="text-sm text-gray-700">
                        • {item.quantity}x {item.product_name} ({(item.price * item.quantity).toFixed(2)} ₺)
                      </div>
                    ))}
                  </div>
                </div>

                {/* İptal Nedeni */}
                {order.special_note && order.special_note.trim() !== "" && (
                  <div className="bg-red-100 border-2 border-red-300 rounded-lg p-4">
                    <p className="text-sm font-bold text-red-800 mb-2">📝 İptal Nedeni:</p>
                    <p className="text-base text-red-900 font-medium">
                      {order.special_note.replace("İptal Nedeni: ", "")}
                    </p>
                  </div>
                )}
                
                {/* İptal nedeni yoksa uyarı */}
                {(!order.special_note || order.special_note.trim() === "") && (
                  <div className="bg-yellow-100 border-2 border-yellow-300 rounded-lg p-3">
                    <p className="text-sm text-yellow-800">⚠️ İptal nedeni belirtilmemiş</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;